import {
  isParamSegment,
  parseRoutePath,
  routeParamsFromSteps,
  routePathFromSteps,
  splitPath,
  type RouteNode,
  type RouteParams,
  type RouteStep,
} from '@features/navigation/logic/routeCatalog'
import type { SidebarTableSort } from '@features/settings/stores/useSettingsStore'
import { splitGridViewPath } from '@features/tables/logic/gridViewPath'
import type { SidebarFavorite } from '../api/contracts'

/** One address per page: a leading slash, no empty or trailing segments. */
export function normalizeFavoritePath(path: string): string {
  return `/${splitPath(path).join('/')}`
}

/**
 * `pathname` is the favorite's page or a page under it (a table's view is
 * under the table), so opening it counts as opening the favorite.
 */
export function isPathWithin(pathname: string, favoritePath: string): boolean {
  const page = splitPath(pathname)
  const favorite = splitPath(favoritePath)
  return (
    favorite.length <= page.length && favorite.every((segment, index) => page[index] === segment)
  )
}

/**
 * The page a page header's heart favorites: what the page is about, not the
 * tab it's on. Where the header has route tabs, the address they share (an
 * automation, whichever of its tabs is open), or its first tab's when that
 * isn't a page; otherwise the address down to its last item that is a page
 * of its own (a table, from any of its views). A view is a way of looking at
 * its page, as a tab is, so a grid page's heart favorites the page, not the
 * view in its address. Null for a page the catalog leaves out (a new
 * automation).
 */
export function pageFavoritePath(
  root: RouteNode,
  pathname: string,
  tabPaths: readonly string[] = []
): string | null {
  const { pagePath, viewId } = splitGridViewPath(pathname)
  if (viewId !== null && parseRoutePath(root, pathname).complete) {
    return pageFavoritePath(root, pagePath, tabPaths)
  }
  if (tabPaths.length > 1) {
    const tabs = tabPaths.map((tabPath) => splitPath(tabPath.split(/[?#]/)[0]))
    const shared: string[] = []
    for (
      let index = 0;
      tabs.every((tab) => tab[index] !== undefined && tab[index] === tabs[0][index]);
      index += 1
    ) {
      shared.push(tabs[0][index])
    }
    for (const candidate of [shared, tabs[0]]) {
      const parsed = parseRoutePath(root, `/${candidate.join('/')}`)
      if (candidate.length > 0 && parsed.complete) return routePathFromSteps(parsed.steps)
    }
  }
  const parsed = parseRoutePath(root, pathname)
  if (!parsed.complete) return null
  for (let index = parsed.steps.length - 1; index >= 0; index -= 1) {
    const step = parsed.steps[index]
    if (isParamSegment(step.node.segment) && step.node.isPage) {
      return routePathFromSteps(parsed.steps.slice(0, index + 1))
    }
  }
  return routePathFromSteps(parsed.steps)
}

/** Between the parts of a favorite's name, as in the homepage setting's trail. */
export const FAVORITE_NAME_SEPARATOR = ' › '

/** How a favorite's page stands now. */
export type FavoriteStatus =
  /** Named from its page's metadata. */
  | 'ok'
  /**
   * Its item is gone (deleted, or out of the user's reach) or the app no longer has the
   * page. Hidden, not removed: it shows again if the user's access comes back.
   */
  | 'gone'
  /** Its area is switched off for the user; it shows again if the area comes back. */
  | 'unavailable'
  /** Its names couldn't be loaded (offline, an error); shown as far as known. */
  | 'unknown'

export interface FavoriteDescription {
  path: string
  /** Its trail as one line, joined with FAVORITE_NAME_SEPARATOR: for sorting and reading out. */
  name: string
  /** Its trail, step by step: its area first, its own page last. */
  parts: string[]
  status: FavoriteStatus
  /** The area it belongs to, by its first segment (`tables`). */
  area: string
  /** It's a table's view. */
  isView: boolean
}

export interface DescribeFavoriteDeps {
  /** A parameter's values with their names, or null when they can't be had. */
  loadParamOptions: (
    paramNode: RouteNode,
    params: RouteParams
  ) => Promise<readonly { value: string; label: string }[] | null>
  /** The signed-in user can open this top-level area (`tables`). */
  isAreaAvailable: (segment: string) => boolean
  /** A fixed segment's name (`Views`, an area's navigation label). */
  segmentLabel: (node: RouteNode) => string
}

/**
 * A fixed segment that names nothing of its own, left out of a favorite's
 * name: a collection just before the item it holds (`table-views` before a
 * view), and a page that is an item's own page when the item has none (a
 * view's `records`). The area always names itself, first.
 */
function isUnnamedSegment(steps: readonly RouteStep[], index: number): boolean {
  const step = steps[index]
  const next = steps[index + 1]
  if (index === 0) return false
  if (next && isParamSegment(next.node.segment)) return true
  const previous = steps[index - 1]
  return (
    !next &&
    isParamSegment(previous.node.segment) &&
    !previous.node.isPage &&
    previous.node.statics[0] === step.node
  )
}

/**
 * Names a favorite from its page's metadata, so a rename shows at once: its
 * area, then each item along its address by its current name and fixed pages
 * by theirs, joined with ` › ` as the homepage setting reads an address
 * (`Tables › Accounts › All Records`, `Automations › Runs`).
 */
export async function describeFavorite(
  root: RouteNode,
  path: string,
  deps: DescribeFavoriteDeps
): Promise<FavoriteDescription> {
  const parsed = parseRoutePath(root, path)
  const steps = parsed.steps
  const area = splitPath(path)[0] ?? ''
  const isView = steps.some((step) => step.node.segment === ':viewId')
  const base = { path, area, isView }

  if (!parsed.complete) return { ...base, name: path, parts: [path], status: 'gone' }
  if (area && !deps.isAreaAvailable(area))
    return { ...base, name: path, parts: [path], status: 'unavailable' }

  const hasItems = steps.some((step) => isParamSegment(step.node.segment))
  const labels: string[] = []
  let status: FavoriteStatus = 'ok'

  for (let index = 0; index < steps.length; index += 1) {
    const step = steps[index]
    if (!isParamSegment(step.node.segment)) {
      if (hasItems && isUnnamedSegment(steps, index)) continue
      const label = deps.segmentLabel(step.node)
      // A step that repeats the one before it stays: Automations › Automations
      // is the Automations list in the Automations area.
      labels.push(label)
      continue
    }
    const options = await deps.loadParamOptions(
      step.node,
      routeParamsFromSteps(steps.slice(0, index))
    )
    if (options === null) {
      status = 'unknown'
      labels.push(decodeURIComponent(step.value))
      continue
    }
    const option = options.find((candidate) => candidate.value === step.value)
    if (!option) return { ...base, name: path, parts: [path], status: 'gone' }
    labels.push(option.label)
  }

  return { ...base, name: labels.join(FAVORITE_NAME_SEPARATOR), parts: labels, status }
}

const byName = (a: FavoriteDescription, b: FavoriteDescription) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true })

/**
 * Favorites in the chosen order: Custom keeps the user's own (the order
 * added, unless reordered), Name is alphabetical, and Recency puts the one
 * opened last first, then those never opened in their own order.
 */
export function sortFavorites(
  favorites: readonly SidebarFavorite[],
  descriptions: ReadonlyMap<string, FavoriteDescription>,
  sort: SidebarTableSort
): SidebarFavorite[] {
  if (sort === 'custom') return [...favorites]
  if (sort === 'name') {
    return [...favorites].sort((a, b) => {
      const left = descriptions.get(a.path)
      const right = descriptions.get(b.path)
      return left && right ? byName(left, right) : 0
    })
  }
  const opened = (favorite: SidebarFavorite) =>
    favorite.last_opened_at ? Date.parse(favorite.last_opened_at) : null
  return [...favorites].sort((a, b) => {
    const left = opened(a)
    const right = opened(b)
    if (left === null || right === null) return left === right ? 0 : left === null ? 1 : -1
    return right - left
  })
}
